import { useMemo, useState, useRef, KeyboardEvent, DragEvent } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronUp, ChevronDown, Search, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface DualListboxItem {
  id: string;
  label: string;
}

interface DualListboxProps {
  items: DualListboxItem[];
  value: string[];
  onChange: (next: string[]) => void;
  availableTitle?: string;
  selectedTitle?: string;
  disabled?: boolean;
}

type Column = "available" | "selected";

interface DropHint {
  column: Column;
  /** Insertion index in the visible list of that column. */
  index: number;
}

export function DualListbox({
  items,
  value,
  onChange,
  availableTitle = "Available Items",
  selectedTitle = "Selected Items",
  disabled = false,
}: DualListboxProps) {
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [availableHighlight, setAvailableHighlight] = useState<string | null>(null);
  const [selectedHighlight, setSelectedHighlight] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragFrom, setDragFrom] = useState<Column | null>(null);
  const [drop, setDrop] = useState<DropHint | null>(null);
  const dragImageEl = useRef<HTMLDivElement | null>(null);

  const byId = useMemo(() => {
    const m = new Map<string, DualListboxItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const selectedItems = useMemo(
    () => value.map((id) => byId.get(id)).filter(Boolean) as DualListboxItem[],
    [value, byId],
  );
  const availableItems = useMemo(() => {
    const selectedSet = new Set(value);
    return items.filter((i) => !selectedSet.has(i.id));
  }, [items, value]);

  const filterFn = (q: string) => (it: DualListboxItem) =>
    q.trim() === "" || it.label.toLowerCase().includes(q.trim().toLowerCase());

  const availableVisible = useMemo(
    () => availableItems.filter(filterFn(availableSearch)),
    [availableItems, availableSearch],
  );
  const selectedVisible = useMemo(
    () => selectedItems.filter(filterFn(selectedSearch)),
    [selectedItems, selectedSearch],
  );

  // ---- Mutations ----

  const moveRight = (ids: string[]) => {
    if (disabled || ids.length === 0) return;
    const set = new Set(value);
    const additions = ids.filter((id) => !set.has(id));
    if (additions.length === 0) return;
    onChange([...value, ...additions]);
    setAvailableHighlight(null);
  };

  const moveLeft = (ids: string[]) => {
    if (disabled || ids.length === 0) return;
    const set = new Set(ids);
    onChange(value.filter((id) => !set.has(id)));
    setSelectedHighlight(null);
  };

  const moveRowInSelected = (id: string, delta: -1 | 1) => {
    if (disabled) return;
    const idx = value.indexOf(id);
    if (idx === -1) return;
    const next = [...value];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const reorderSelected = (id: string, insertIndex: number) => {
    if (disabled) return;
    const current = [...value];
    const from = current.indexOf(id);
    if (from === -1) return;
    current.splice(from, 1);
    const adjusted = insertIndex > from ? insertIndex - 1 : insertIndex;
    current.splice(Math.max(0, Math.min(current.length, adjusted)), 0, id);
    onChange(current);
  };

  const addToSelectedAt = (id: string, insertIndex: number) => {
    if (disabled) return;
    if (value.includes(id)) {
      reorderSelected(id, insertIndex);
      return;
    }
    const next = [...value];
    next.splice(Math.max(0, Math.min(next.length, insertIndex)), 0, id);
    onChange(next);
  };

  // ---- Drag handlers ----

  const startDrag = (e: DragEvent<HTMLDivElement>, id: string, from: Column) => {
    if (disabled) return;
    setDragId(id);
    setDragFrom(from);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", id);
    } catch {
      /* noop */
    }
  };

  const endDrag = () => {
    setDragId(null);
    setDragFrom(null);
    setDrop(null);
  };

  const computeDropIndex = (
    e: DragEvent<HTMLDivElement>,
    listEl: HTMLDivElement | null,
    visibleCount: number,
  ): number => {
    if (!listEl) return visibleCount;
    const rows = Array.from(listEl.querySelectorAll<HTMLElement>("[data-row='1']"));
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) return i;
    }
    return rows.length;
  };

  const onColumnDragOver = (
    e: DragEvent<HTMLDivElement>,
    column: Column,
    listEl: HTMLDivElement | null,
    visibleCount: number,
  ) => {
    if (disabled || !dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const index = computeDropIndex(e, listEl, visibleCount);
    setDrop((prev) =>
      prev && prev.column === column && prev.index === index ? prev : { column, index },
    );
  };

  const onColumnDrop = (e: DragEvent<HTMLDivElement>, column: Column) => {
    if (disabled || !dragId) return;
    e.preventDefault();
    const id = dragId;
    const insertIndex = drop?.column === column ? drop.index : column === "selected" ? value.length : 0;

    if (column === "selected") {
      if (dragFrom === "selected") {
        reorderSelected(id, insertIndex);
      } else {
        addToSelectedAt(id, insertIndex);
      }
    } else if (column === "available" && dragFrom === "selected") {
      moveLeft([id]);
    }
    endDrag();
  };

  // ---- Keyboard ----

  const onRowKey = (
    e: KeyboardEvent<HTMLDivElement>,
    id: string,
    column: Column,
  ) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (column === "available") moveRight([id]);
      else moveLeft([id]);
    }
  };

  // ---- Refs for drop index calc ----
  const availableListRef = useRef<HTMLDivElement | null>(null);
  const selectedListRef = useRef<HTMLDivElement | null>(null);

  const renderCount = (visible: number, total: number) => {
    if (visible === total) return total.toString();
    return `${visible} / ${total}`;
  };

  const DropLine = ({ active }: { active: boolean }) => (
    <div
      aria-hidden
      className={cn(
        "h-0.5 -my-px rounded-full transition-all",
        active ? "bg-primary" : "bg-transparent",
      )}
    />
  );

  const renderRow = (
    item: DualListboxItem,
    column: Column,
    indexInVisible: number,
    isHighlighted: boolean,
    extraRight?: React.ReactNode,
  ) => {
    const isDragging = dragId === item.id;
    const showDropAbove = drop?.column === column && drop.index === indexInVisible && dragId !== item.id;
    return (
      <div key={item.id} className="select-none">
        <DropLine active={!!showDropAbove} />
        <div
          data-row="1"
          role="option"
          aria-selected={isHighlighted}
          tabIndex={disabled ? -1 : 0}
          draggable={!disabled}
          onDragStart={(e) => startDrag(e, item.id, column)}
          onDragEnd={endDrag}
          onClick={() => {
            if (column === "available") setAvailableHighlight(item.id);
            else setSelectedHighlight(item.id);
          }}
          onDoubleClick={() => {
            if (column === "available") moveRight([item.id]);
            else moveLeft([item.id]);
          }}
          onKeyDown={(e) => onRowKey(e, item.id, column)}
          className={cn(
            "group flex items-center gap-2 rounded-md border border-transparent px-2.5 py-2 text-sm transition-all",
            "hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            isHighlighted && "bg-primary/10 border-primary/30 text-foreground",
            isDragging && "opacity-60 rotate-1 scale-[0.98] shadow-lg cursor-grabbing",
            !disabled && "cursor-grab",
            disabled && "opacity-60 cursor-not-allowed",
          )}
        >
          <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
          {extraRight}
        </div>
      </div>
    );
  };

  const ColumnShell = ({
    title,
    column,
    search,
    setSearch,
    visible,
    total,
    listRef,
    children,
  }: {
    title: string;
    column: Column;
    search: string;
    setSearch: (s: string) => void;
    visible: number;
    total: number;
    listRef: React.RefObject<HTMLDivElement>;
    children: React.ReactNode;
  }) => {
    const isDropTarget = drop?.column === column && !!dragId && dragFrom !== column ? true : false;
    return (
      <div
        className={cn(
          "flex-1 flex flex-col rounded-lg border border-border bg-card transition-all",
          isDropTarget && "ring-2 ring-primary/40 border-primary/40",
        )}
      >
        <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <Badge variant="secondary" className="font-mono text-[10px]">
              {renderCount(visible, total)}
            </Badge>
          </div>
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSearch("");
              }}
              placeholder="Search…"
              className="h-8 pl-7 text-sm"
              disabled={disabled}
            />
          </div>
        </div>
        <div
          ref={listRef}
          onDragOver={(e) => onColumnDragOver(e, column, listRef.current, visible)}
          onDrop={(e) => onColumnDrop(e, column)}
          className="flex-1 min-h-[220px] max-h-[360px] overflow-y-auto px-2 pb-2"
        >
          {children}
          {/* Trailing drop zone */}
          <DropLine active={drop?.column === column && drop.index === visible} />
          {visible === 0 && (
            <div className="text-xs text-muted-foreground text-center py-8">
              {total === 0 ? "Empty" : "No matches"}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---- Main render ----
  return (
    <div className="flex flex-col md:flex-row items-stretch gap-3">
      {/* Available */}
      <ColumnShell
        title={availableTitle}
        column="available"
        search={availableSearch}
        setSearch={setAvailableSearch}
        visible={availableVisible.length}
        total={availableItems.length}
        listRef={availableListRef}
      >
        {availableVisible.map((it, i) =>
          renderRow(it, "available", i, availableHighlight === it.id),
        )}
      </ColumnShell>

      {/* Controls */}
      <div className="flex md:flex-col items-center justify-center gap-2 md:px-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          disabled={disabled || availableVisible.length === 0}
          onClick={() => moveRight(availableVisible.map((i) => i.id))}
          aria-label="Move all to selected"
          title="Move all"
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          disabled={disabled || !availableHighlight}
          onClick={() => availableHighlight && moveRight([availableHighlight])}
          aria-label="Move selected to selected"
          title="Move selected"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          disabled={disabled || !selectedHighlight}
          onClick={() => selectedHighlight && moveLeft([selectedHighlight])}
          aria-label="Move selected to available"
          title="Remove selected"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          disabled={disabled || selectedVisible.length === 0}
          onClick={() => moveLeft(selectedVisible.map((i) => i.id))}
          aria-label="Move all to available"
          title="Remove all"
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      </div>

      {/* Selected */}
      <ColumnShell
        title={selectedTitle}
        column="selected"
        search={selectedSearch}
        setSearch={setSelectedSearch}
        visible={selectedVisible.length}
        total={selectedItems.length}
        listRef={selectedListRef}
      >
        {selectedVisible.map((it, i) => {
          const absoluteIndex = value.indexOf(it.id);
          const isFirst = absoluteIndex === 0;
          const isLast = absoluteIndex === value.length - 1;
          return renderRow(
            it,
            "selected",
            i,
            selectedHighlight === it.id,
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
              <button
                type="button"
                disabled={disabled || isFirst}
                onClick={(e) => {
                  e.stopPropagation();
                  moveRowInSelected(it.id, -1);
                }}
                className="p-1 rounded hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Move ${it.label} up`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                disabled={disabled || isLast}
                onClick={(e) => {
                  e.stopPropagation();
                  moveRowInSelected(it.id, 1);
                }}
                className="p-1 rounded hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Move ${it.label} down`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>,
          );
        })}
      </ColumnShell>

      {/* Hidden drag image holder (no-op, kept for potential future use) */}
      <div ref={dragImageEl} className="sr-only" aria-hidden />
    </div>
  );
}

export default DualListbox;
